import { provideHttpClient } from '@angular/common/http';
import {
    ApplicationConfig,
    provideZonelessChangeDetection,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
    withRouterConfig,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideTransloco } from 'app/core/transloco/transloco.provider';
import 'moment/locale/fr';
import { provideQuillConfig } from 'ngx-quill';
import { provideToastr } from 'ngx-toastr';
import QuillVideo from 'quill/formats/video';
import { provideApiUrl } from './core/http-interceptor/api-url.provider';
import { providePwa } from './core/pwa/pwa.provider';

// Eliminate the html method so that convertHTML does not turn the iframe into a anchor tag
// which it then fails to convert back to an iframe when it loads.
export class MyQuillVideo extends QuillVideo {
    html = null as any;
}

// and in my ngx-quill options:
// customModules: [
//     { path: 'formats/video', implementation: MyQuillVideo }
// ]


export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideToastr({
            preventDuplicates: true,
            progressBar: true,
        }),
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withRouterConfig({ paramsInheritanceStrategy: 'always' })
        ),


        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'fr_FR',
        },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'DD/MM/YYYY',
                    timeInput: 'HH:mm',  // <- required
                },
                display: {
                    dateInput: 'DD/MM/YYYY',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                    timeInput: 'HH:mm', // <- required
                    timeOptionLabel: 'HH:mm', // <- required
                },
            },
        },
        provideQuillConfig({
            customModules: [
                {
                    path: 'formats/video',
                    implementation: MyQuillVideo
                }
            ]
        }),
        // Transloco Config
        provideTransloco(),

        // Fuse
        provideAuth(),
        provideApiUrl(),
        provideIcons(),
        providePwa(),
        provideFuse({
            fuse: {
                layout: 'modern',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-sky',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-sky',
                        name: 'Sky',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
